/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React from 'react';
import { Card } from '@g17eco/atoms/card';
import { Badge, Button } from 'reactstrap';
import type { InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { SWOT_COLORS, PILLAR_COLORS, TIME_HORIZON_LABELS, CONTROL_LEVEL_LABELS } from '../utils/constants';
import {
  getPriorityInfo,
  getPillarIcon,
  getTimeHorizonIcon,
  getControlLevelIcon,
  getImpactInfo,
  getSeverityIcon,
  getSwotIcon,
} from '../utils/helpers';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import ClampLines from 'react-clamp-lines';

interface Props {
  insight: InsightSynthesis;
  onDetailsClick?: () => void;
  truncateTitle?: boolean;
  truncateDescription?: boolean;
}

export const InsightCard: React.FC<Props> = ({ insight, onDetailsClick, truncateTitle = true, truncateDescription = true }) => {
  const { content, source } = insight;
  const pillarColor = PILLAR_COLORS[source.metadata.sectionType] || 'secondary';
  const swotColor = SWOT_COLORS[content.swotCategory] || 'secondary';
  const priorityInfo = getPriorityInfo(content.priorityScore);

  const borderColor = `background-Theme${pillarColor.charAt(0).toUpperCase()}${pillarColor.slice(1)}Dark`;

  return (
    <Card shadow='sm' className='insight-card position-relative'>
      {/* Corner Accent for Pillar */}
      <div
        className={`position-absolute ${borderColor}`}
        style={{
          top: 0,
          right: 0,
          width: '3rem',
          height: '3rem',
          clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
          zIndex: 0,
        }}
      />
      <i
        className={`fas ${getPillarIcon(source.metadata.sectionType)} position-absolute text-ThemeTextWhite`}
        style={{
          top: '0.5rem',
          right: '0.5rem',
          fontSize: '0.75rem',
          zIndex: 1,
        }}
      />

      <div className='p-3 position-relative' style={{ zIndex: 2, minWidth: 0 }}>
        {/* Header: SWOT Category (hero) + Score badge */}
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            <SimpleTooltip text='SWOT Category: Strategic classification (Strength, Weakness, Opportunity, or Threat)'>
              <Badge color={swotColor} className='text-uppercase text-sm px-3'>
                <i className={`fas ${getSwotIcon(content.swotCategory)} me-2`} />
                {content.swotCategory}
              </Badge>
            </SimpleTooltip>
            <div className='text-ThemeTextMedium text-uppercase'>{source.categoryName}</div>
          </div>
          <SimpleTooltip text='Priority Score: Overall importance ranking based on impact, urgency, and strategic alignment'>
            <Badge color={priorityInfo.color} className='px-2 py-1 me-5'>
              {priorityInfo.label}
            </Badge>
          </SimpleTooltip>
        </div>

        {/* Metadata Row: Time Horizon, Control Level, Impact */}
        <div className='d-flex flex-wrap gap-2 mb-3'>
          <SimpleTooltip text='Time Horizon: When the impact of this insight is expected to materialize'>
            <div className='d-flex align-items-center text-ThemeTextMedium text-sm'>
              <i className={`fas ${getTimeHorizonIcon(content.timeHorizon)} me-1`} />
              <span>{TIME_HORIZON_LABELS[content.timeHorizon]}</span>
            </div>
          </SimpleTooltip>
          <SimpleTooltip text='Control Level: How much control the organization has over addressing this insight'>
            <div className='d-flex align-items-center text-ThemeTextMedium text-sm'>
              <i className={`fas ${getControlLevelIcon(content.controlLevel)} me-1`} />
              <span>Control: {CONTROL_LEVEL_LABELS[content.controlLevel]}</span>
            </div>
          </SimpleTooltip>
          <SimpleTooltip text='Impact: The severity or significance of this insight to the organization'>
            <div className='d-flex align-items-center text-sm'>
              <i
                className={`fas ${getImpactInfo(content.impact).icon} me-1 text-${getImpactInfo(content.impact).color}`}
              />
              <span className={`text-${getImpactInfo(content.impact).color}`}>Impact: {content.impact}</span>
            </div>
          </SimpleTooltip>
        </div>

        {/* Title */}
        <h5
          className={`text-ThemeTextDark strong text-lg mb-2 ${truncateTitle ? 'overflow-hidden text-truncate text-nowrap min-w-0' : ''}`}
          style={onDetailsClick ? { cursor: 'pointer' } : {}}
          onClick={onDetailsClick}
        >
          {content.title}
        </h5>

        {/* Description */}
        {truncateDescription ? (
          <div style={{ minHeight: '5rem' }}>
            <ClampLines
              id={`description-${insight.batchInsightId}`}
              text={content.description}
              lines={3}
              ellipsis='...'
              buttons={false}
              className='text-ThemeTextMedium text-md mb-3'
            />
          </div>
        ) : (
          <p className='text-ThemeTextMedium text-md mb-3'>{content.description}</p>
        )}

        {/* Impact Assessment */}
        <SimpleTooltip text='Impact Assessment: Breakdown of potential impacts across key organizational dimensions'>
          <div className='border-top pt-2 text-ThemeTextMedium text-sm mb-2'>Impact Assessment</div>
        </SimpleTooltip>
        <div className='d-flex ms-auto me-auto gap-5 mb-3 w-100' style={{ maxWidth: '400px' }}>
          <div className='flex-fill text-center'>
            <div className='text-sm text-left fw-medium mb-2 text-ThemeAccentDark'>
              Financial
              <SimpleTooltip text='Financial Impact: Potential effect on revenue, costs, and profitability'>
                <i className='fas fa-info-circle ms-1' />
              </SimpleTooltip>
            </div>
            <div
              className='background-ThemeBgToolbar border-radius'
              style={{ height: '8px', position: 'relative', overflow: 'hidden' }}
            >
              <div
                className='h-100 background-ThemeAccentLight'
                style={{
                  width: `${content.impactAssessment.financial * 10}%`,
                }}
              />
            </div>
          </div>
          <div className='flex-fill text-center'>
            <div className='text-sm text-left fw-medium mb-2 text-ThemeDangerDark'>
              Regulatory
              <SimpleTooltip text='Regulatory Impact: Implications for compliance and regulatory requirements'>
                <i className='fas fa-info-circle ms-1' />
              </SimpleTooltip>
            </div>
            <div
              className='background-ThemeBgToolbar border-radius'
              style={{ height: '8px', position: 'relative', overflow: 'hidden' }}
            >
              <div
                className='h-100 background-ThemeDangerLight'
                style={{
                  width: `${content.impactAssessment.regulatory * 10}%`,
                }}
              />
            </div>
          </div>
          <div className='flex-fill text-center'>
            <div className='text-sm text-left fw-medium mb-2 text-ThemeWarningDark'>
              Reputational
              <SimpleTooltip text='Reputational Impact: Effect on brand image and stakeholder perception'>
                <i className='fas fa-info-circle ms-1' />
              </SimpleTooltip>
            </div>
            <div
              className='background-ThemeBgToolbar border-radius'
              style={{ height: '8px', position: 'relative', overflow: 'hidden' }}
            >
              <div
                className='h-100 background-ThemeWarningLight'
                style={{
                  width: `${content.impactAssessment.reputational * 10}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Key Findings */}
        {content.keyFindings && content.keyFindings.length > 0 && (
          <div className='border-top pt-2 mb-3'>
            <h6 className='text-sm text-ThemeTextMedium'>KEY FINDINGS</h6>
            <div className='d-flex flex-column gap-2'>
              {content.keyFindings.slice(0, 2).map((finding, index) => {
                const severityStyle = getSeverityIcon(finding.severity);
                return (
                  <div key={index} className='d-flex'>
                    <div className='ms-1'>
                      <i
                        className={`fas ${severityStyle.icon} mr-2 text-xs`}
                        style={{ marginTop: '0.1rem', color: severityStyle.color }}
                      />
                    </div>
                    <div className='text-ThemeTextLight mt-1'>{finding.text}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className='border-top background-ThemeBgMedium p-2'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <div className='d-flex gap-3 text-sm text-ThemeTextMedium'>
              {content.recommendations.length > 0 && (
                <div>
                  <i className='fas fa-tasks me-1' />
                  {content.recommendations.length} recommendation{content.recommendations.length !== 1 ? 's' : ''}
                </div>
              )}
              {content.references && content.references.length > 0 && (
                <div>
                  <i className='fas fa-link me-1' />
                  {content.references.length} reference{content.references.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            {onDetailsClick && (
              <Button color='link' className='px-0' size='sm' onClick={onDetailsClick}>
                <i className='fas fa-external-link-alt me-1' />
                Details
              </Button>
            )}
          </div>
          {/* AI Quality Metrics - Subtle */}
          <div className='d-flex gap-3 text-sm text-ThemeTextLight'>
            <SimpleTooltip text="Confidence: AI's confidence in the accuracy and validity of this insight">
              <span>
                <i className='fas fa-certificate me-1' style={{ fontSize: '0.65rem' }} />
                {Math.round(content.confidence * 100)}%
              </span>
            </SimpleTooltip>
            <SimpleTooltip text="Relevance: How relevant this insight is to the organization's goals and context">
              <span>
                <i className='fas fa-bullseye me-1' style={{ fontSize: '0.65rem' }} />
                {Math.round(content.relevance * 100)}%
              </span>
            </SimpleTooltip>
            <SimpleTooltip text='Clarity: How clear and well-defined the insight is'>
              <span>
                <i className='fas fa-eye me-1' style={{ fontSize: '0.65rem' }} />
                {Math.round(content.clarity * 100)}%
              </span>
            </SimpleTooltip>
          </div>
        </div>
      </div>
    </Card>
  );
};
